import styles from "./ClienteEditModal.module.css";
import React, { useState, useEffect, useCallback } from "react";
import { ModalProps } from "@/pages/Dashboard/Clientes/models/ModalProps.ts";
import api from "@/utils/connector/ApiConnector.ts";
import { useUser } from "@/utils/userContext/UserContext.tsx";
import { documentHelper } from "@/utils/common/documentHelper.tsx";
import { phoneNumberHelper } from "@/utils/common/phoneNumberHelper.tsx";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { RegisterStatuses } from "@/pages/Dashboard/Clientes/models/RegisterStatuses";
import { ProductApi } from "@/utils/models/Product";
import { useGlobalModal } from "../../../../components/GlobalModalProvider/GlobalModalProvider";
import { X } from "lucide-react";
import { showApiErrorModal } from "@/utils/common/apiErrorHelper";
import AssociateModal from "./AssociateModal";
import { Pencil, Trash2 } from "lucide-react";

interface AffiliateOption {
  id: string;
  name: string | null;
  email: string;
}


const initialFields = [
  { label: "Empresa", value: "", formField: "name", required: false },
  { label: "Email", value: "", formField: "email", required: true },
  { label: "Telefone", value: "", formField: "phone", required: true },
  { label: "CNPJ da Empresa", value: "", formField: "companyDocument", required: false },
  { label: "Caixa Postal", value: "", formField: "postalBoxNumber", required: false },
  { label: "Representante Legal", value: "", formField: "legalRepresentativeName", required: false },
  { label: "CPF do Representante Legal", value: "", formField: "legalRepresentativeDocument", required: false },
  { label: "Data de Nascimento", value: "", formField: "birthDate", required: false },
  { label: "Endereço", value: "", formField: "address", required: false },
  { label: "Produto", value: "", formField: "productName", required: true },
  { label: "Data de Início", value: "", formField: "startDate", required: true },
  { label: "Data de Término", value: "", formField: "endDate", required: true },
  { label: "Afiliado", value: "", formField: "affiliateId", required: false }
];

const ClientRegisterModal: React.FC<ModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState(initialFields);
  const [contractStartDate, setContractStartDate] = useState<string>("");
  const [contractEndDate, setContractEndDate] = useState<string>("");
  const [customerBirthDate, setCustomerBirthDate] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const userContext = useUser();
  const userData = userContext?.userData;
  const [products, setProducts] = useState<ProductApi[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState<string>(""); // será enviado no body
  
  const [associates, setAssociates] = useState<any[]>([]);
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState<any | null>(null);
  const [editingAssociateIndex, setEditingAssociateIndex] = useState<number | null>(null);

  // Affiliate search state
  const [affiliateOptions, setAffiliateOptions] = useState<AffiliateOption[]>([]);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<AffiliateOption | null>(null);
  const [affiliateInputValue, setAffiliateInputValue] = useState("");

useEffect(() => {
  let mounted = true;
  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      // envia params e o header Authorization (muito importante)
      const resp = await api.get('/product', {
        params: { page: 1, pageSize: 500 },
        headers: { Authorization: `Bearer ${userData?.auth}` },
      });

      // dependendo do seu wrapper, resp pode ser AxiosResponse ou já o data
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;

      console.log('fetchProducts - raw response:', raw);

      // aceita vários formatos: array puro, { items: [...] }, { data: [...] }, { products: [...] }
      let list: any[] = [];
      if (Array.isArray(raw.items)) {
        list = raw.items;
      } else if (Array.isArray(raw)) {
        list = raw;
      } else {
        // fallback: tenta extrair arrays de qualquer prop
        const maybeArray = Object.values(raw).find(v => Array.isArray(v));
        if (Array.isArray(maybeArray)) list = maybeArray as any[];
      }
      if (!mounted) return;
      setProducts(list);
      if (list.length > 0) {
        setSelectedProductName(list[0].name);
      } else {
        setSelectedProductName('');
      }
    } catch (err: any) {
      console.error('Erro ao buscar products:', err);
      // mostra mensagem de erro útil (401, 403, 5xx)
      const status = err?.response?.status;
      const message = err?.response?.data ?? err.message;
      console.warn('Status:', status, 'Message:', message);
    } finally {
      if (mounted) setProductsLoading(false);
    }
  };

  // execute quando modal abrir ou quando userData mudar
  if (isOpen) fetchProducts();

  return () => { mounted = false; };
}, [isOpen, userData?.auth]);

// Search affiliates function
const searchAffiliates = useCallback(async (query: string) => {
  if (!userData?.auth) return;
  
  setAffiliateLoading(true);
  try {
    const resp = await api.get('/customer/search-affiliates', {
      params: { query, limit: 20 },
      headers: { Authorization: `Bearer ${userData.auth}` },
    });
    
    const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
    const list = Array.isArray(raw) ? raw : [];
    setAffiliateOptions(list);
  } catch (err: any) {
    console.error('Erro ao buscar afiliados:', err);
    setAffiliateOptions([]);
  } finally {
    setAffiliateLoading(false);
  }
}, [userData?.auth]);

// Debounce affiliate search
useEffect(() => {
  if (!isOpen) return;
  
  const timeoutId = setTimeout(() => {
    if (affiliateInputValue.length >= 1) {
      searchAffiliates(affiliateInputValue);
    } else {
      // Load initial options when input is empty
      searchAffiliates("");
    }
  }, 300);

  return () => clearTimeout(timeoutId);
}, [affiliateInputValue, isOpen, searchAffiliates]);

// Reset form when modal opens
useEffect(() => {
  if (isOpen) {
    setFields(initialFields);
    setContractStartDate("");
    setContractEndDate("");
    setCustomerBirthDate("");
    setSelectedAffiliate(null);
    setAffiliateInputValue("");
    setAffiliateOptions([]);
    setAssociates([]);
  }
}, [isOpen]);


  if (!isOpen) return null;

  const handleOpenAssociateModal = (associate: any | null, index: number | null = null) => {
    setSelectedAssociate(associate);
    setEditingAssociateIndex(index);
    setAssociateModalOpen(true);
  };

  const handleCloseAssociateModal = () => {
    setAssociateModalOpen(false);
    setSelectedAssociate(null);
    setEditingAssociateIndex(null);
  };

  const handleSaveAssociate = (associate: any) => {
    if (editingAssociateIndex !== null) {
      const updatedAssociates = [...associates];
      updatedAssociates[editingAssociateIndex] = associate;
      setAssociates(updatedAssociates);
    } else {
      setAssociates([...associates, associate]);
    }
    handleCloseAssociateModal();
  };

  const handleDeleteAssociate = (index: number) => {
    const updatedAssociates = associates.filter((_, i) => i !== index);
    setAssociates(updatedAssociates);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFields(prev =>
      prev.map(field => {
        if (field.formField === name) {
          if (name === "phone") {
            return { ...field, value: phoneNumberHelper.formatPhone(value.replace(/\D/g, "")) };
          }
          if (name === "companyDocument") {
            return { ...field, value: documentHelper.formatCNPJ(value.replace(/\D/g, "")) };
          }
          if (name === "legalRepresentativeDocument") {
            return { ...field, value: documentHelper.formatCPF(value.replace(/\D/g, "")) };
          }
          // Os demais campos aceitam texto livre
          return { ...field, value };
        }
        return field;
      })
    );
    if (name === "productName") {
      setSelectedProductName(value);
    }
  };

const makeRequestBody = () => {
  const formItem: any = {};

  fields.forEach(field => {
    switch (field.formField) {
      case "name":
        formItem.name = field.value ?? "";
        break;
      case "email":
        formItem.email = field.value ?? "";
        break;
      case "phone":
        formItem.phone = phoneNumberHelper.cleanPhone(field.value ?? "");
        break;
      case "companyDocument":
        formItem.companyDocument = documentHelper.cleanDocument(field.value ?? "");
        break;
      case "postalBoxNumber":
        formItem.postalBoxNumber = field.value ?? "";
        break;
      case "legalRepresentativeName":
        formItem.legalRepresentativeName = field.value ?? "";
        break;
      case "legalRepresentativeDocument":
        formItem.legalRepresentativeDocument = documentHelper.cleanDocument(field.value ?? "");
        break;
      case "birthDate":
        formItem.birthDate = customerBirthDate || null;
        break;
      case "address":
        formItem.address = field.value ?? "";
        break;
      case "productName":
        formItem.productName = selectedProductName || field.value || "";
        break;
      case "affiliateId":
        formItem.affiliateId = selectedAffiliate?.id || null;
        break;
      case "startDate":
        formItem.startDate = contractStartDate || field.value || "";
        break;
      case "endDate":
        formItem.endDate = contractEndDate || field.value || "";
        break;
      default:
        break;
    }
  });

  formItem.associates = associates.map(a => ({
    name: a.name,
    email: a.email,
    phone: phoneNumberHelper.cleanPhone(a.phone),
  }));

  return { customers: [formItem] };
};

//modal de erro genérico
  const modal = useGlobalModal();

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);

  // Validate required fields
  const missingFields: string[] = [];
  
  fields.forEach(field => {
    if (field.required) {
      if (field.formField === "startDate" && !contractStartDate) {
        missingFields.push(field.label);
      } else if (field.formField === "endDate" && !contractEndDate) {
        missingFields.push(field.label);
      } else if (field.formField === "productName" && !selectedProductName) {
        missingFields.push(field.label);
      } else if (field.formField !== "startDate" && field.formField !== "endDate" && field.formField !== "productName") {
        const value = field.value?.trim();
        if (!value) {
          missingFields.push(field.label);
        }
      }
    }
  });

  if (missingFields.length > 0) {
    const message = `Campos obrigatórios faltando: ${missingFields.join(", ")}`;
    if (modal) {
      modal.show({ type: "warning", title: "Não foi possível cadastrar", message, autoClose: 4000 });
    } else {
      window.alert(message);
    }
    setLoading(false);
    return;
  }

  const emailValue = fields.find((field) => field.formField === "email")?.value?.trim() ?? "";
  const phoneValue = phoneNumberHelper.cleanPhone(
    fields.find((field) => field.formField === "phone")?.value ?? "",
  );

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
    const message = "Informe um email válido para continuar.";
    if (modal) {
      modal.show({ type: "warning", title: "Email inválido", message, autoClose: 4000 });
    } else {
      window.alert(message);
    }
    setLoading(false);
    return;
  }

  if (phoneValue.length < 10) {
    const message = "Informe um telefone com DDD válido para continuar.";
    if (modal) {
      modal.show({ type: "warning", title: "Telefone inválido", message, autoClose: 4000 });
    } else {
      window.alert(message);
    }
    setLoading(false);
    return;
  }

  if (!selectedProductName) {
    const message = "Nenhum produto disponível para associar. Por favor, crie um produto primeiro.";
    if (modal) {
      modal.show({ type: "warning", title: "Não foi possível cadastrar", message, autoClose: 3000 });
    } else {
      window.alert(message);
    }
    setLoading(false);
    return;
  }

  if (contractStartDate && contractEndDate && contractEndDate < contractStartDate) {
    const message = "A data de termino precisa ser igual ou posterior a data de inicio.";
    if (modal) {
      modal.show({ type: "warning", title: "Datas invalidas", message, autoClose: 4000 });
    } else {
      window.alert(message);
    }
    setLoading(false);
    return;
  }

  const body = makeRequestBody();

  try {
    const token = userData?.auth;
    //nesse caso estou usando um postRaw para pegar o response completo
    //e conseguir pegar a resposta caso em de erro, 200,201,400
    const resp = await api.postRaw("/customer", body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

// após obter resp, status, data:
const status = resp.status;
const data = resp.data as any;
console.log("create customer resp:", status, data);

// 1) erro HTTP
if (status >= 400) {
  showApiErrorModal(modal, { response: { data } }, {
    title: "Erro ao criar cliente",
    fallbackMessage: `Erro inesperado (HTTP ${status})`,
  });
  setLoading(false);
  return;
}

// 2) heurísticas de criação
// backend pode não fornecer createdCount; tentamos deduzir:
// - createdCount (explícito) > 0 => sucesso
// - ou status === 201 e customers array tem items => sucesso
// - caso contrário, se subscriptions criadas mas cliente não criado => aviso
const createdCount = Number(data?.createdCount ?? 0);
const customers = Array.isArray(data?.customers) ? data.customers : [];
const subscriptions = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
const subscriptionsCount = Number(data?.subscriptionsCount ?? subscriptions.length ?? 0);

// Sucesso claro: createdCount > 0 OR status === 201 e customers.length > 0
if (createdCount > 0 || (status === 201 && customers.length > 0)) {
  const successMessage = "Cliente inserido com sucesso!";
  if (modal) {
    modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 2500 });
  } else {
    window.alert(successMessage);
  }
  onSuccess?.();
  onClose?.();
  setLoading(false);
  return;
}

// Caso parcial: subscriptions criadas mas não há indicação de cliente novo
if (subscriptionsCount > 0 && (createdCount === 0 && customers.length > 0)) {
  const message = `Operação concluída. ${customers.length} cliente(s) encontrados/retornados. Foram criadas ${subscriptionsCount} inscrição(ões).`;
  if (modal) {
    modal.show({
      type: "warning",
      title: "Operação parcial",
      message,
      actions: [
        { label: "Fechar" },
        { label: "Ver inscrições", onClick: () => window.location.href = "/dashboard/inscricoes", variant: "primary" },
      ],
      autoClose: null,
    });
  } else {
    window.alert(message);
  }
  setLoading(false);
  return;
}

// Fallback: se não conseguimos inferir, mostramos resumo do body
const fallbackMsg = `Operação finalizada. Clientes: ${customers.length}, Inscrições: ${subscriptionsCount}. Verifique detalhes no console.`;
if (modal) {
  modal.show({ type: "info", title: "Resultado", message: fallbackMsg, autoClose: null });
} else {
  window.alert(fallbackMsg);
}
console.log("Resultado create customer (fallback):", data);
setLoading(false);

  } catch (err: any) {
    console.error("Erro ao criar cliente (catch):", err);

    // extrair info do error.response se houver
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (status && status >= 400) {
      // mensagem amigável
      showApiErrorModal(modal, err, {
        title: "Erro ao criar cliente",
        fallbackMessage: `Erro servidor (HTTP ${status})`,
      });
    } else {
      showApiErrorModal(modal, err, {
        title: "Erro ao criar cliente",
        fallbackMessage: "Erro ao criar cliente",
      });
    }
    setLoading(false);
  }
}

  const handleTogglePassword = () => setShowPassword(prev => !prev);
  const isWideField = (formField: string) => ["address", "affiliateId", "productName"].includes(formField);
  const isDateField = (formField: string) => ["birthDate", "startDate", "endDate"].includes(formField);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <form className={styles.clienteExpandAreaForm} onSubmit={handleSubmit}>
          <div className={styles.modalHeader}>
            <div>
              <h2 className={styles.modalTitle}>Adicionar novo cliente</h2>
              <p className={styles.modalSubtitle}>
                Preencha os dados para cadastrar o cliente e vincular a inscricao.
              </p>
            </div>
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fechar modal">
              <X size={18} />
            </button>
          </div>
          <p className={styles.requiredLegend}>
            <span className={styles.requiredMark}>*</span> Campos obrigatorios
          </p>

          <div className={styles.formFieldsContainer}>
            {fields.map(field => {
              switch (field.formField) {
                case "startDate":
                  return (
                    <label
                      key={field.formField + "-startDate"}
                      className={`${styles.formField} ${isWideField(field.formField) ? styles.fullWidthField : ""} ${isDateField(field.formField) ? styles.dateField : ""}`}
                    >
                      <span className={styles.fieldLabel}>
                        {field.label}{field.required && <span className={styles.requiredMark}>*</span>}
                      </span>
                      <input
                        type="date"
                        className={styles.inputField}
                        value={contractStartDate}
                        onChange={(e) => setContractStartDate(e.target.value)}
                        required
                      />
                    </label>
                  );
                case "endDate":
                  return (
                    <label
                      key={field.formField + "-endDate"}
                      className={`${styles.formField} ${isWideField(field.formField) ? styles.fullWidthField : ""} ${isDateField(field.formField) ? styles.dateField : ""}`}
                    >
                      <span className={styles.fieldLabel}>
                        {field.label}{field.required && <span className={styles.requiredMark}>*</span>}
                      </span>
                      <input
                        type="date"
                        className={styles.inputField}
                        value={contractEndDate}
                        onChange={(e) => setContractEndDate(e.target.value)}
                        required
                      />
                    </label>
                  );
                  case "productName":
                    return (
                      <label
                        key={field.formField + "-productName"}
                        className={`${styles.formField} ${isWideField(field.formField) ? styles.fullWidthField : ""}`}
                      >
                        <span className={styles.fieldLabel}>
                          {field.label}{field.required && <span className={styles.requiredMark}>*</span>}
                        </span>
                        <select
                          className={styles.inputField}
                          name={field.formField}
                          value={selectedProductName}
                          onChange={handleChange}
                          disabled={productsLoading}
                          required={field.required}
                        >
                          <option value="">Selecione um produto...</option>
                          {products.map(product => (
                            <option key={product.name + "-option"} value={product.name}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                case "birthDate":
                  return (
                    <label
                      key={field.formField + "-birthDate"}
                      className={`${styles.formField} ${isDateField(field.formField) ? styles.dateField : ""}`}
                    >
                      <span className={styles.fieldLabel}>{field.label}</span>
                      <input
                        type="date"
                        className={styles.inputField}
                        value={customerBirthDate}
                        onChange={(e) => setCustomerBirthDate(e.target.value)}
                      />
                    </label>
                  );
                  case "affiliateId":
                    return (
                      <label
                        key={field.formField + "-affiliateId"}
                        className={`${styles.formField} ${isWideField(field.formField) ? styles.fullWidthField : ""}`}
                      >
                        <span className={styles.fieldLabel}>{field.label}</span>
                        <Autocomplete
                          options={affiliateOptions}
                          getOptionLabel={(option) =>
                            option.name
                              ? `${option.name} (${option.email})`
                              : option.email
                          }
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={selectedAffiliate}
                          onChange={(_, newValue) => setSelectedAffiliate(newValue)}
                          inputValue={affiliateInputValue}
                          onInputChange={(_, newInputValue) => setAffiliateInputValue(newInputValue)}
                          loading={affiliateLoading}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontWeight: 500 }}>{option.name || "Sem nome"}</span>
                                <span style={{ fontSize: "0.85em", color: "#666" }}>{option.email}</span>
                              </div>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Buscar por nome ou email..."
                              variant="outlined"
                              size="small"
                              className={styles.muiFieldInput}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {affiliateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          noOptionsText="Nenhum cliente encontrado"
                          loadingText="Buscando..."
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                            }
                          }}
                        />
                      </label>
                    );
                default:
                  return (
                    <label
                      key={field.formField + "-" + field.label}
                      className={`${styles.formField} ${isWideField(field.formField) ? styles.fullWidthField : ""}`}
                    >
                      <span className={styles.fieldLabel}>
                        {field.label}{field.required && <span className={styles.requiredMark}>*</span>}
                      </span>
                      <input
                        className={styles.inputField}
                        name={field.formField}
                        value={field.value ?? ""}
                        onChange={handleChange}
                        placeholder={field.label}
                        required={field.required}
                      />
                    </label>
                  );
              }
            })}
          </div>

          <div style={{ marginTop: 20 }}>
            <div className={styles.flexSeparator} style={{ marginBottom: 10 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>Associados</h3>
              <button
                type="button"
                onClick={() => handleOpenAssociateModal(null)}
                className={styles.primaryButton}
              >
                Adicionar Associado
              </button>
            </div>
            {associates.length === 0 ? (
              <p>Nenhum associado adicionado.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {associates.map((associate, index) => (
                    <tr key={index}>
                      <td>{associate.name}</td>
                      <td>{associate.email}</td>
                      <td>{associate.phone}</td>
                      <td>
                        <button type="button" onClick={() => handleOpenAssociateModal(associate, index)} className={styles.iconButton}><Pencil size={16} /></button>
                        <button type="button" onClick={() => handleDeleteAssociate(index)} className={styles.iconButton}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className={styles.clienteExpandAreaButtons}>
            <button type="button" className={styles.secondaryButton} onClick={onClose}>Cancelar</button>
            <button type="submit" className={styles.primaryButton} disabled={loading}>
              {loading ? "Salvando..." : "Salvar cliente"}
            </button>
          </div>
        </form>
      </div>

      {associateModalOpen && (
        <AssociateModal
          open={associateModalOpen}
          onClose={handleCloseAssociateModal}
          onSave={handleSaveAssociate}
          associate={selectedAssociate}
          customerId="" // Not needed for new customer
        />
      )}

    </div>
  );
};

export default ClientRegisterModal;
